import { useEffect, useState } from 'react';
import { api, type ApiError } from '../api/client';
import { Alert } from './Field';
import { PreviewBody, type Preview } from './PreviewModal';

/**
 * Shows a content item's current version to a reviewer.
 *
 * Text content comes inline from the body endpoint; a file goes through the
 * same short-lived signed link learners get, so a file still in malware
 * quarantine cannot be opened here either.
 */
export function ContentPreviewModal({
  item,
  onClose,
}: {
  item: { id: string; title: string; kind: string };
  onClose: () => void;
}) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const base = { id: item.id, title: item.title, type: item.kind, courseTitle: '' };
    const textual = item.kind === 'RICH_TEXT' || item.kind === 'CODE';
    const load = textual
      ? api<{ body: string; language: string | null }>(`/content/${item.id}/body`).then(
          (result): Preview => ({
            ...base,
            body: result.body,
            language: result.language ?? undefined,
          }),
        )
      : api<{ url: string; mimeType: string | null }>(`/content/${item.id}/link`).then(
          (result): Preview => ({
            ...base,
            streamUrl: result.url,
            mimeType: result.mimeType ?? undefined,
          }),
        );
    load.then(setPreview).catch((caught) => setError(caught as ApiError));
  }, [item.id, item.kind, item.title]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal__backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal__head">
          <span className="modal__title">{item.title}</span>
          <button className="modal__close" onClick={onClose} aria-label="Close preview">
            ×
          </button>
        </div>

        <div className="modal__body">
          {error ? <Alert kind="error" title={error.message} /> : null}
          {!preview && !error ? <div className="spinner">Loading preview…</div> : null}
          {preview ? <PreviewBody preview={preview} /> : null}
        </div>
      </div>
    </div>
  );
}
